import { createSelectSchema, FieldType, Schema } from "../../../../toolympus/hooks/useSchema";

export enum AbsenceKind {
  Remote = 'remote',
}

export enum ApprovalStatus {
  Approve = 'approve',
  Reject = 'reject',
}

export interface AuditFields {
  created_at?: string; // ISO Datetime string
  created_by?: string; // UUID string
  updated_at?: string; // ISO Datetime string
  updated_by?: string; // UUID string
}

// Interface for AbsenceApproval items in the `approvals` array
export interface AbsenceApproval {
  approver_id: string;
  approver_user_id: string;
  created_at: string; // Stored as :str in Clojure definition, likely ISO Datetime string
  is_employee_manager: boolean;
  approval_status: ApprovalStatus;
  comment?: string;
}

export interface AbsenceEvent extends AuditFields {
  _id: number;
  kind: AbsenceKind | string; 
  
  title?: string;
  description?: any; // Stored as :jsonb, can be updated to specific interface if structure is known
  comment?: string;
  
  start_date: string; // ISO Date string (YYYY-MM-DD)
  end_date: string;   // ISO Date string (YYYY-MM-DD)
  
  owner_id: string;

  b24_process_id?: string;
  b24_element_id?: string;

  cancelled_at?: string; // ISO Datetime string
  cancelled_by?: string;
  
  approvals?: AbsenceApproval[];
  approval_status?: ApprovalStatus | string;
  approval_log?: string;
}

export const AbsenceKindNote = "note";


export const AbsenceEventSchema: Schema = {
  kind: createSelectSchema([
    { value: "remote", label: "Онлайн" },
    { value: "study", label: "Учеба" },
    { value: "businesstrip", label: "Командировка" },
    { value: "vacation", label: "Отпуск" },
    { value: "dayoff", label: "Свободный день" },
    { value: "compensation_dayoff", label: "Отгул за выходные" },
    { value: "sickleave", label: "Больничный" },
    { value: "overtime", label: "Работа в выходной" },
    { value: "office", label: "Работа в офисе" },
    { value: AbsenceKindNote, label: "Комментарий" },
  ], { 
    label: "Тип", 
  }),
  
  title: { 
    label: "Название", 
  },
  
  description: { 
    type: FieldType.powerdoc,
    label: "Описание", 
  },
  
  comment: { 
    type: FieldType.textlong, 
    label: "Комментарий", 
  },
  
  approval_log: { 
    type: FieldType.textlong, 
    label: "Лог согласований", 
  },
  
  start_date: { 
    type: FieldType.date, 
    label: "Начало" 
  },
  
  end_date: { 
    type: FieldType.date, 
    label: "Конец" 
  },
  
  owner_id: { 
    type: FieldType.dictionarySelect,
    label: "Сотрудник",
    dictionary: "Employees",
  },
  
  cancelled_at: { 
    type: FieldType.datetime, 
    label: "Отменено", 
  },
  
  cancelled_by: { 
    dictionary: "Users",
    type: FieldType.dictionarySelect, 
    label: "Отменено", 
  },
  
  created_at: { 
    type: FieldType.datetime, 
    label: "Создано", 
  },
  
  created_by: { 
    dictionary: "Users",
    type: FieldType.dictionarySelect, 
    label: "Создано", 
  },
  
};



export interface AbsenceStats {
  year: number;
  stats: {
    dayoff: {
      taken: number;
      limit: number;
      remaining: number;
    };
    vacation: {
      taken: number;
    };
    // Index signature to leave room for future additions (like sickleave, etc.)
    [kind: string]: {
      taken: number;
      limit?: number;
      extra?: number;
      remaining?: number;
    };
  };
}



export const getEventColor = (kind: string, isCancelled?: boolean) => {
  if (isCancelled) {
    return 'rgba(180, 180, 180, 0.5)';
  }
  switch (kind) {
    case 'remote':
      return 'rgba(33, 150, 243, 0.5)';
    case 'study':
      return 'rgba(127, 255, 7, 0.5)';
    case 'businesstrip':
      return 'rgba(0, 150, 136, 0.5)'; 
    case 'vacation':
      return 'rgba(255, 152, 0, 0.5)';
    case 'dayoff':
      return 'rgba(255, 193, 7, 0.5)';
    case 'compensation_dayoff':
      return 'rgba(233, 30, 99, 0.5)';
    case 'sickleave':
      return 'rgba(244, 67, 54, 0.5)'; // Red
    case 'overtime':
      return 'rgba(76, 175, 80, 0.5)';
    case 'office':
      return 'rgba(76, 175, 80, 0.5)';
    default:
      return 'rgba(33, 150, 243, 0.5)'; // Blue fallback
  }
};

export const ABSENCE_KIND_LETTERS: Record<string, string> = {
  remote: 'удл',
  study: 'учб',
  businesstrip: 'кмн',
  vacation: 'отп',
  dayoff: 'свб',
  compensation_dayoff: 'отг',
  sickleave: 'бол',
  overtime: 'вхд',
  office: 'офс',
};




export interface AbsenceExtraQuota {
  _id: number;
  employee_id: string; // UUID
  kind: string;
  year: string;
  days: number;
  comment: string;

  created_at: string; // ISO Datetime string
  created_by: string; // UUID string
  updated_at?: string; // ISO Datetime string
  updated_by?: string; // UUID string
}

export const AbsenceExtraQuotaSchema: Schema = {
  _id: { label: "ID" },

  employee_id: {
    type: FieldType.dictionarySelect,
    dictionary: "Employees",
    label: "Сотрудник",
  },

  kind: AbsenceEventSchema.kind,

  year: { label: "Год" },

  days: {
    type: FieldType.number,
    label: "Количество дней",
  },

  comment: {
    type: FieldType.textlong,
    label: "Комментарий",
  },

  created_at: { type: FieldType.datetime, label: "Создан" },
  created_by: {
    type: FieldType.dictionarySelect,
    dictionary: "Users",
    label: "Кем создан",
  },
  updated_at: { type: FieldType.datetime, label: "Обновлен" },
  updated_by: {
    type: FieldType.dictionarySelect,
    dictionary: "Users",
    label: "Кем обновлен",
  },
};